import { FilterState, Item, Priority, TaskType } from './types';

/** Known sources from the Miro Source multiselect. Free-form values are allowed. */
export const SOURCES: string[] = ['Customer', 'Dragos', 'Matt'];

/** Sentinel value for the assignee filter's "Unassigned" option (matches items with no assignee). */
export const UNASSIGNED = '__unassigned__';

export const emptyFilters = (): FilterState => ({ search: '', statuses: [], priorities: [], types: [], assignees: [], sources: [], labels: [] });

/** The presentational + ordinal bits of a priority, resolved for a value. */
export interface PriorityMeta {
  value: Priority;
  label: string;
  /** Hex colour of the chevron mark (gray / blue / yellow / red). */
  color: string;
  /** Chevron count: 0 = a dash (low), 1–3 = stacked up-chevrons. */
  level: number;
  /** Ordinal rank for sorting (low = 0 … blocker = 3). */
  rank: number;
}

/**
 * The fixed, ordinal priority set (low < medium < high < blocker). Priority is a
 * client-side global enum (NOT per-board data like statuses): a dash for low, then
 * 1–3 chevrons tinted gray / blue / yellow / red. Kept in `rank` order.
 */
export const PRIORITIES: PriorityMeta[] = [
  { value: 'low', label: 'Low', color: '#a1a1aa', level: 0, rank: 0 },
  { value: 'medium', label: 'Medium', color: '#60a5fa', level: 1, rank: 1 },
  { value: 'high', label: 'High', color: '#f6b91a', level: 2, rank: 2 },
  { value: 'blocker', label: 'Blocker', color: '#ef4444', level: 3, rank: 3 },
];

/** What a new item defaults to (mirrors the backend column default). */
export const DEFAULT_PRIORITY: Priority = 'medium';

const PRIORITY_BY_VALUE = new Map<string, PriorityMeta>(PRIORITIES.map((p) => [p.value, p]));

/** Neutral fallback for an unknown priority value (mirrors fallbackStatusMeta) — the
 *  server returns a bare string, so guard against a value outside the known set. */
const fallbackPriorityMeta = (value: string): PriorityMeta => ({
  value: (value || 'medium') as Priority,
  label: value || 'Unknown',
  color: '#a1a1aa',
  level: 0,
  rank: PRIORITIES.length,
});

/** Colour/label/level/rank for a priority value, falling back gracefully. */
export const priorityMeta = (value: string): PriorityMeta => PRIORITY_BY_VALUE.get(value) ?? fallbackPriorityMeta(value);

/** Ordinal rank for sorting; unknown/blank sorts last (after blocker). */
export const priorityRank = (value: string): number => PRIORITY_BY_VALUE.get(value)?.rank ?? PRIORITIES.length;

/** The presentational + ordinal bits of a task type, resolved for a value. */
export interface TaskTypeMeta {
  value: TaskType;
  label: string;
  /** Hex colour of the type's glyph badge (JIRA-style: indigo/purple/green/blue/red). */
  color: string;
  /** Ordinal rank, broad → narrow (initiative = 0 … bug = 4), for sorting. */
  rank: number;
}

/**
 * The fixed task-type set (Initiative > Epic > Story / Task / Bug). Like {@link PRIORITIES} it
 * is a global, client-side enum (NOT per-board data like statuses). Each renders as a small
 * coloured glyph badge (see TaskTypeGlyph in ui/icons). Kept in `rank` order.
 */
export const TASK_TYPES: TaskTypeMeta[] = [
  { value: 'initiative', label: 'Initiative', color: '#6366f1', rank: 0 },
  { value: 'epic', label: 'Epic', color: '#a855f7', rank: 1 },
  { value: 'story', label: 'Story', color: '#22c55e', rank: 2 },
  { value: 'task', label: 'Task', color: '#3b82f6', rank: 3 },
  { value: 'bug', label: 'Bug', color: '#ef4444', rank: 4 },
];

/** What a new item defaults to (mirrors the backend column default). */
export const DEFAULT_TASK_TYPE: TaskType = 'task';

/** Item types that never render as a row/card in the board views (Table, Kanban, Timeline) — they
 *  have their own UI (epics live in the left rail). Their children render promoted to the nearest
 *  visible ancestor. Single source of truth so all three views hide the same set. */
export const BOARD_HIDDEN_TYPES = new Set<TaskType>(['epic', 'initiative']);
export const hiddenFromBoard = (it: { type: string }): boolean => BOARD_HIDDEN_TYPES.has(it.type as TaskType);

const TASK_TYPE_BY_VALUE = new Map<string, TaskTypeMeta>(TASK_TYPES.map((t) => [t.value, t]));

/** Neutral fallback for an unknown type value (mirrors fallbackPriorityMeta). */
const fallbackTaskTypeMeta = (value: string): TaskTypeMeta => ({
  value: (value || 'task') as TaskType,
  label: value || 'Unknown',
  color: '#a1a1aa',
  rank: TASK_TYPES.length,
});

/** Colour/label/rank for a task-type value, falling back gracefully. */
export const taskTypeMeta = (value: string): TaskTypeMeta => TASK_TYPE_BY_VALUE.get(value) ?? fallbackTaskTypeMeta(value);

/** Ordinal rank for sorting; unknown/blank sorts last. */
export const taskTypeRank = (value: string): number => TASK_TYPE_BY_VALUE.get(value)?.rank ?? TASK_TYPES.length;

/**
 * Statuses are now per-board data (see {@link ../statuses}); this palette is what the
 * status colour picker offers when creating or recolouring a column. The first five are
 * the original default status colours; the rest extend the set. Any hex is still allowed
 * (the picker also has a free colour input).
 */
export const STATUS_PALETTE: string[] = [
  '#a1a1aa', '#2ec5e8', '#f6b91a', '#ff6163', '#34d399', '#a78bfa',
  '#f472b6', '#60a5fa', '#fb923c', '#f59e0b', '#10b981', '#ef4444',
];

/**
 * Swatches the epic colour picker offers when tinting an epic (sidebar rail). The first entry is
 * the default epic purple, so picking it is the same as "no custom colour". Distinct from
 * STATUS_PALETTE so epic tints don't read as status colours.
 */
export const EPIC_COLORS: string[] = [
  '#a855f7', '#6366f1', '#3b82f6', '#06b6d4', '#14b8a6', '#22c55e',
  '#84cc16', '#eab308', '#f59e0b', '#f97316', '#ef4444', '#ec4899',
];

/** The colour an epic renders with: its own chosen tint, else the default epic-type colour.
 *  Single source of truth for the epic chip, the sidebar glyph, and any focus affordance. */
export const epicColor = (epic?: Item | null): string => epic?.color || taskTypeMeta('epic').color;
